import { useState, useMemo, useCallback, useEffect } from "react";
import { useEmployees } from "@/hooks/useEmployees";
import { useOptimizedData } from "@/hooks/useOptimizedData";
import { AdvancedSearch, SearchConfig } from "@/components/ui/search-advanced";
import { OptimizedTable, TableColumn } from "@/components/ui/optimized-table";
import { DataExport } from "@/components/ui/data-export";
import { EmployeeDialog } from "@/components/employees/EmployeeDialog";
import { EmployeeDetailsView } from "@/components/employees/EmployeeDetailsView";
import { EmployeeApprovalDialog } from "@/components/employees/EmployeeApprovalDialog";
import { Employee } from "@/types/employee";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Plus, Edit, Trash2, Users, Phone, UserCheck, Eye, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { PageWrapper } from "@/components/layout/PageWrapper";


export default function Employees() {
  const [viewMode, setViewMode] = useState<'table' | 'cards' | 'details'>('table');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);

  const { employees, loading, uploading, saveEmployee, deleteEmployee, error } = useEmployees();

  // تكوين البحث المتقدم
  const searchConfig: SearchConfig = useMemo(() => ({
    placeholder: "البحث في الموظفين...",
    filters: [
      {
        id: 'department',
        label: 'القسم',
        type: 'select',
        options: [
          { value: 'hr', label: 'الموارد البشرية' },
          { value: 'sales', label: 'المبيعات' },
          { value: 'marketing', label: 'التسويق' },
          { value: 'finance', label: 'المالية' },
          { value: 'it', label: 'تقنية المعلومات' }
        ],
        icon: Users
      },
      {
        id: 'status',
        label: 'حالة الوصول',
        type: 'select',
        options: [
          { value: 'present', label: 'مسموح' },
          { value: 'absent', label: 'مرفوض' },
          { value: 'late', label: 'معلق' }
        ],
        icon: UserCheck
      },
      {
        id: 'approval_status',
        label: 'حالة الموافقة',
        type: 'select',
        options: [
          { value: 'pending', label: 'معلق' },
          { value: 'approved', label: 'معتمد' },
          { value: 'rejected', label: 'مرفوض' }
        ],
        icon: CheckCircle
      }
    ]
  }), []);

  // استخدام hook البيانات المحسن
  const {
    data: displayEmployees,
    allData: filteredEmployees,
    searchQuery,
    setSearchQuery,
    filters,
    addFilter,
    removeFilter,
    clearFilters,
    sortConfig,
    changeSort,
    pagination,
    changePage,
    stats
  } = useOptimizedData({
    data: employees,
    searchFields: ['name', 'phone', 'department', 'status'],
    initialPageSize: 20
  });

  // معالجات الأحداث
  const handleAddEmployee = useCallback(() => {
    setSelectedEmployee(null);
    setIsDialogOpen(true);
  }, []);

  const handleEditEmployee = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setIsDialogOpen(true);
  }, []);

  const handleDeleteEmployee = useCallback((employee: Employee) => {
    if (confirm(`هل أنت متأكد من حذف الموظف "${employee.name}"؟`)) {
      deleteEmployee(employee);
      toast.success("تم حذف الموظف بنجاح");
    }
  }, [deleteEmployee]);

  const handleViewDetails = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setViewMode('details');
  }, []);

  const handleBackToList = useCallback(() => {
    setViewMode('table');
    setSelectedEmployee(null);
  }, []);

  const handleApprovalDialog = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setIsApprovalDialogOpen(true);
  }, []);

  const handleApprovalChange = useCallback(() => {
    window.location.reload();
  }, []);

  // تكوين أعمدة الجدول
  const tableColumns: TableColumn<Employee>[] = useMemo(() => [
    {
      key: 'name',
      title: 'اسم الموظف',
      dataIndex: 'name',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-card-foreground">{value}</div>
      )
    },
    {
      key: 'phone',
      title: 'رقم الجوال',
      dataIndex: 'phone',
      render: (value) => value || '-'
    },
    {
      key: 'department',
      title: 'القسم',
      dataIndex: 'department',
      sortable: true
    },
    {
      key: 'status',
      title: 'حالة الوصول',
      dataIndex: 'status',
      sortable: true,
      render: (value, record) => {
        const statusConfig = {
          'present': { label: 'مسموح', color: 'text-green-600' },
          'absent': { label: 'مرفوض', color: 'text-red-600' },
          'late': { label: 'معلق', color: 'text-yellow-600' }
        };
        
        const handleStatusChange = async (newStatus: string) => {
          try {
            // تحديد حالة الموافقة بناء على حالة الوصول
            const approvalStatus = newStatus === 'present' ? 'approved' : 'pending';
            
            const { error } = await supabase
              .from('employees')
              .update({ 
                status: newStatus,
                approval_status: approvalStatus
              })
              .eq('id', record.id);
            
            if (error) throw error;
            window.location.reload();
            toast.success("تم تحديث حالة الوصول والموافقة بنجاح");
          } catch (error) {
            toast.error("حدث خطأ أثناء تحديث الحالة");
          }
        };
        
        return (
          <Select value={value} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[120px] h-8">
              <SelectValue>
                <span className={statusConfig[value as keyof typeof statusConfig]?.color || 'text-gray-600'}>
                  {statusConfig[value as keyof typeof statusConfig]?.label || value}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="present">
                <span className="text-green-600">مسموح</span>
              </SelectItem>
              <SelectItem value="absent">
                <span className="text-red-600">مرفوض</span>
              </SelectItem>
              <SelectItem value="late">
                <span className="text-yellow-600">معلق</span>
              </SelectItem>
            </SelectContent>
          </Select>
        );
      }
    },
    {
      key: 'approval_status',
      title: 'حالة الموافقة',
      dataIndex: 'approval_status',
      sortable: true,
      render: (value) => {
        const statusConfig = {
          'pending': { label: 'معلق', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
          'approved': { label: 'معتمد', color: 'bg-green-100 text-green-800 border-green-300' },
          'rejected': { label: 'مرفوض', color: 'bg-red-100 text-red-800 border-red-300' }
        };
        const config = statusConfig[value as keyof typeof statusConfig] || statusConfig['pending'];
        return (
          <Badge 
            variant="outline" 
            className={`text-xs border ${config.color}`}
          >
            {config.label}
          </Badge>
        );
      }
    },
    {
      key: 'actions',
      title: 'الإجراءات',
      align: 'center',
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewDetails(record)}
            className="h-8 w-8 p-0"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {record.approval_status === 'pending' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleApprovalDialog(record)}
              className="h-8 w-8 p-0"
            >
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditEmployee(record)}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteEmployee(record)}
            className="h-8 w-8 p-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ], [handleViewDetails, handleApprovalDialog, handleEditEmployee, handleDeleteEmployee]);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل الموظفين..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-center items-center h-64 flex-col space-y-4">
          <div className="text-lg text-destructive">حدث خطأ في تحميل البيانات</div>
          <Button onClick={() => window.location.reload()} variant="outline">
            <RefreshCw className="w-4 h-4 ml-2" />
            إعادة المحاولة
          </Button>
        </div>
      </div>
    );
  }

  if (viewMode === 'details' && selectedEmployee) {
    return (
      <div className="container mx-auto p-6 space-y-6" dir="rtl">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-card-foreground">تفاصيل الموظف</h1>
          <Button onClick={handleBackToList} variant="outline">
            العودة للقائمة
          </Button>
        </div>
        <EmployeeDetailsView employee={selectedEmployee} />
      </div>
    );
  }

  return (
    <PageWrapper
      title="إدارة الموظفين"
      subtitle="عرض وإدارة بيانات الموظفين والصلاحيات"
      maxWidth="2xl"
      actions={
        <>
          <Button 
            onClick={handleAddEmployee} 
            size="sm"
            className="gap-2 w-full sm:w-auto"
          >
            <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="text-xs sm:text-sm">إضافة موظف</span>
          </Button>
          
          <div className="hidden sm:flex text-xs text-muted-foreground gap-3">
            <span>المجموع: {stats.total}</span>
            <span>المفلتر: {stats.filtered}</span>
            <span>المعروض: {stats.showing}</span>
          </div>
        </>
      }
    >
      <AdvancedSearch
        config={searchConfig}
        onSearch={(query, searchFilters) => {
          setSearchQuery(query);
          clearFilters();
          searchFilters.forEach(filter => {
            addFilter(filter.id, filter.value, filter.type);
          });
        }}
      />

      {/* Stats for mobile */}
      <div className="flex sm:hidden text-xs text-muted-foreground gap-3 justify-center py-2">
        <span>المجموع: {stats.total}</span>
        <span>المفلتر: {stats.filtered}</span>
        <span>المعروض: {stats.showing}</span>
      </div>

      <OptimizedTable
        columns={tableColumns}
        data={displayEmployees}
        loading={loading}
        sortConfig={sortConfig}
        onSortChange={(config) => config && changeSort(config.key as keyof Employee, config.direction)}
        className="rounded-lg border shadow-sm overflow-hidden"
      />

      {displayEmployees.length === 0 && !loading && (
        <div className="text-center py-8 sm:py-12 text-muted-foreground">
          <Users className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50" />
          <p className="text-sm sm:text-base">لا توجد بيانات موظفين</p>
        </div>
      )}

      <DataExport
        data={filteredEmployees}
        filename="employees"
        columns={[
          { key: 'name', title: 'الاسم' },
          { key: 'email', title: 'البريد الإلكتروني' },
          { key: 'phone', title: 'رقم الهاتف' },
          { key: 'department', title: 'القسم' },
          { key: 'status', title: 'حالة الوصول' },
          { key: 'approval_status', title: 'حالة الموافقة' },
        ]}
      />

      <EmployeeDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        editingEmployee={selectedEmployee}
        onSubmit={saveEmployee}
        uploading={uploading}
      />

      <EmployeeApprovalDialog
        open={isApprovalDialogOpen}
        onOpenChange={setIsApprovalDialogOpen}
        employee={selectedEmployee}
        onApprovalChange={handleApprovalChange}
      />
    </PageWrapper>
  );
}